import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Player, GameRole } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import PhaseHeader from "./PhaseHeader";
import { ReactComponent as CitizenIcon } from "../../assets/images/user.svg";
import { ReactComponent as MafiaIcon } from "../../assets/images/mafia.svg";
import { ReactComponent as DonIcon } from "../../assets/don_light.svg";
import { ReactComponent as SheriffIcon } from "../../assets/sheriff_light.svg";

interface RoleAssignmentProps {
  language: number;
  players: Player[];
  judge?: Player;
  streamError?: boolean;
  onRolesReady: (roles: GameRole[]) => void;
  onCancel: () => void;
  onTimeoutToggle: () => void;
  // Timer state
  timerAccumulated?: number;
  timerStartedAt?: number;
  onTimerStateChange?: (accumulated: number, startedAt: number | undefined) => void;
}

const PhaseBackground = styled.div`
  position: relative;
  min-height: 100vh;
  padding-bottom: 4rem;
  background: radial-gradient(circle at top, rgba(124, 93, 255, 0.25), transparent 55%),
    radial-gradient(circle at bottom right, rgba(32, 18, 72, 0.6), transparent 60%),
    #080413;
  color: #f5f2ff;
`;

const PhaseContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 2rem 3rem;

  @media (max-width: 992px) {
    padding: 1.5rem 1.5rem 2.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem 1.25rem 2rem;
  }
`;

const PhaseMain = styled.div.attrs({ className: "main" })`
  margin-top: 1.5rem;

  @media (max-width: 768px) {
    margin-top: 1rem !important;
  }
`;

const SummaryCard = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.25rem 1.5rem;
  background: rgba(15, 11, 29, 0.78);
  border: 1px solid rgba(124, 93, 255, 0.22);
  border-radius: 22px;
  box-shadow: 0 14px 42px rgba(8, 5, 20, 0.35);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 600;
  font-size: 0.95rem;

  span {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.75rem;
    opacity: 0.65;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    gap: 0.25rem;
    font-size: 0.85rem;

    span {
      font-size: 0.65rem;
    }
  }
`;

const SummaryValue = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: 1.5rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const SummaryTarget = styled.span`
  font-size: 0.9rem;
  opacity: 0.6;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const PlayersTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(18, 12, 36, 0.94);
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(10, 6, 25, 0.35);
  margin-bottom: 1.5rem;

  thead th {
    padding: 1rem 1.25rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 0.75rem;
    font-weight: 700;
    background: rgba(255, 255, 255, 0.08);
  }

  tbody tr {
    transition: background 0.2s ease;
  }

  tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.02);
  }

  tbody tr:hover {
    background: rgba(124, 93, 255, 0.12);
  }

  tbody td {
    padding: 0.65rem 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: middle;

    @media (max-width: 768px) {
      padding: 0.6rem 0.5rem;
    }
  }
`;

const NumberCell = styled.td`
  width: 50px;
  font-weight: 700;
  font-size: 0.95rem;
`;

const RoleCell = styled.td<{ $visible: boolean }>`
  width: 64px;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  pointer-events: ${(props) => (props.$visible ? "auto" : "none")};
  transition: opacity 0.25s ease;
`;

const RoleIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  cursor: pointer;
  transition: transform 0.2s ease;

  svg,
  img {
    width: 100%;
    height: 100%;
  }

  &:hover {
    transform: translateY(-1px) scale(1.08);
  }
`;

const PlayerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const PlayerAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.15);
`;

const PlayerName = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const RoleName = styled.span`
  font-size: 0.85rem;
  opacity: 0.7;
`;

const RolesToggle = styled.button.attrs({ type: "button" })`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 12px;
  background: rgba(124, 93, 255, 0.15);
  border: 1px solid rgba(124, 93, 255, 0.35);
  color: #d6d0ff;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(124, 93, 255, 0.25);
    border-color: rgba(124, 93, 255, 0.5);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.65rem;
    gap: 0.35rem;
  }
`;

const InstructionsCard = styled.div`
  display: none;
  margin-top: 1.5rem;
  padding: 1.35rem 1.5rem;
  border-radius: 22px;
  background: rgba(255, 196, 0, 0.12);
  border: 1px solid rgba(255, 196, 0, 0.38);
  color: #ffe9a4;
  box-shadow: 0 12px 32px rgba(255, 196, 0, 0.18);

  ul {
    margin: 0.75rem 0 0;
    padding-left: 1.25rem;
  }

  li + li {
    margin-top: 0.35rem;
  }
`;

const ValidationAlert = styled.div`
  margin-top: 1.5rem;
  padding: 1.35rem 1.5rem;
  border-radius: 22px;
  background: rgba(255, 77, 109, 0.18);
  border: 1px solid rgba(255, 77, 109, 0.45);
  color: #ffd0d9;
  box-shadow: 0 12px 32px rgba(255, 77, 109, 0.15);

  strong {
    color: #ffffff;
  }
`;

const ActionsFooter = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
`;

const CancelButton = styled.button`
  padding: 0.85rem 1.75rem;
  border-radius: 18px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  color: #ffffff;
  background: linear-gradient(135deg, #ff4d6d 0%, #ff6b81 100%);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 40px rgba(255, 77, 109, 0.45);
  }

  &:active {
    transform: translateY(0);
  }
`;

const RoleAssignment: React.FC<RoleAssignmentProps> = ({
  language,
  players,
  judge,
  streamError = false,
  onRolesReady,
  onCancel,
  onTimeoutToggle,
  timerAccumulated,
  timerStartedAt,
  onTimerStateChange,
}) => {
  const [playerRoles, setPlayerRoles] = useState<GameRole[]>(
    Array(10).fill("red" as GameRole)
  );
  const [showRoles, setShowRoles] = useState(true);

  const dict = dictionaries[language].ph;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Initialize with all citizens - no default special roles
    const defaultRoles: GameRole[] = Array(10).fill("red");
    setPlayerRoles(defaultRoles);
  }, []);

  const getRoleCounts = () => {
    const counts = {
      red: 0,
      black: 0,
      don: 0,
      sheriff: 0,
    };
    
    playerRoles.forEach((role) => {
      if (role in counts) {
        counts[role as keyof typeof counts]++;
      }
    });
    
    return counts;
  };

  const switchRole = (playerIndex: number) => {
    console.log('switchRole called for player', playerIndex);
    const currentRole = playerRoles[playerIndex];
    const counts = getRoleCounts();
    
    console.log('Current role:', currentRole, 'Counts:', counts);
    
    // Determine available roles based on current counts
    const availableRoles: GameRole[] = [];
    
    // Special roles that still have slots available
    if ((counts.black || 0) < 2 || currentRole === "black") availableRoles.push("black");
    if ((counts.don || 0) < 1 || currentRole === "don") availableRoles.push("don");
    if ((counts.sheriff || 0) < 1 || currentRole === "sheriff") availableRoles.push("sheriff");
    
    // Citizen is always available as fallback
    availableRoles.push("red");
    
    console.log('Available roles:', availableRoles);
    
    // Find current role index in available roles
    const currentIndex = availableRoles.indexOf(currentRole);
    
    // Get next role (cycle through available roles)
    const nextIndex = (currentIndex + 1) % availableRoles.length;
    const nextRole = availableRoles[nextIndex];
    
    console.log('Next role:', nextRole);

    const newRoles = [...playerRoles];
    newRoles[playerIndex] = nextRole;
    setPlayerRoles(newRoles);
  };

  const isValidDistribution = () => {
    const counts = getRoleCounts();
    return (
      counts.red === 6 &&
      counts.black === 2 &&
      counts.don === 1 &&
      counts.sheriff === 1
    );
  };

  const getRoleIcon = (role: GameRole) => {
    switch (role) {
      case "red":
        return <CitizenIcon aria-hidden="true" focusable="false" />;
      case "black":
        return <MafiaIcon aria-hidden="true" focusable="false" />;
      case "don":
        return <DonIcon aria-hidden="true" focusable="false" />;
      case "sheriff":
        return <SheriffIcon aria-hidden="true" focusable="false" />;
      default:
        return <CitizenIcon aria-hidden="true" focusable="false" />;
    }
  };

  const getRoleName = (role: GameRole) => {
    switch (role) {
      case "red":
        return dict.citizen;
      case "black":
        return dict.maf;
      case "don":
        return dict.don;
      case "sheriff":
        return dict.shrf;
      default:
        return dict.citizen;
    }
  };

  const handleNext = () => {
    if (isValidDistribution()) {
      onRolesReady(playerRoles);
    }
  };

  const toggleRoleVisibility = () => {
    setShowRoles(!showRoles);
  };

  const roleCounts = getRoleCounts();

  return (
    <PhaseBackground>
      <PhaseContainer>
        <PhaseHeader
          title={dict.intro_n}
          variant="night"
          language={language}
          streamError={streamError}
          onTimeoutToggle={onTimeoutToggle}
          timerAccumulated={timerAccumulated}
          timerStartedAt={timerStartedAt}
          onTimerStateChange={onTimerStateChange}
          actions={[
            {
              id: "spotify",
              label: "♫",
              onClick: () => {
                 window.location.href = "spotify:";
              },
              variant: "secondary",
            },
            {
              id: "ready",
              label: dict.morning || "Утро",
              onClick: handleNext,
              disabled: !isValidDistribution(),
              variant: "primary",
            },
          ]}
        />

        <PhaseMain>
          <SummaryCard>
            <SummaryItem>
              <span>{dict.citizens}</span>
              <SummaryValue>
                {roleCounts.red || 0}
                <SummaryTarget>/6</SummaryTarget>
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <span>{dict.mafia}</span>
              <SummaryValue>
                {roleCounts.black || 0}
                <SummaryTarget>/2</SummaryTarget>
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <span>{dict.don}</span>
              <SummaryValue>
                {roleCounts.don || 0}
                <SummaryTarget>/1</SummaryTarget>
              </SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <span>{dict.shrf}</span>
              <SummaryValue>
                {roleCounts.sheriff || 0}
                <SummaryTarget>/1</SummaryTarget>
              </SummaryValue>
            </SummaryItem>
          </SummaryCard>

          <PlayersTable>
            <thead>
              <tr>
                <th>#</th>
                <th>{dict.roles}</th>
                <th>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>{dict.player}</span>
                    <RolesToggle onClick={toggleRoleVisibility}>
                      👁️ {showRoles ? dict.hroles : dict.sroles}
                    </RolesToggle>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {players.map((player, index) => (
                <tr key={player.user_id} id={`player${player.user_id}`}>
                  <NumberCell>{index + 1}</NumberCell>
                  <RoleCell $visible={showRoles}>
                    <RoleIcon onClick={() => switchRole(index)}>
                      {getRoleIcon(playerRoles[index])}
                    </RoleIcon>
                  </RoleCell>
                  <td>
                    <PlayerInfo>
                      <PlayerAvatar src={player.avatar} alt={player.nickname} />
                      <div>
                        <PlayerName>{player.nickname}</PlayerName>
                        {showRoles && (
                          <RoleName>({getRoleName(playerRoles[index])})</RoleName>
                        )}
                      </div>
                    </PlayerInfo>
                  </td>
                </tr>
              ))}
            </tbody>
          </PlayersTable>

          <InstructionsCard>
            <strong>{dict.instructions}:</strong>
            <ul>
              <li>{dict.instructions_roles}</li>
              <li>{dict.required_roles}</li>
              <li>{dict.hide_roles_instruction}</li>
              <li>{dict.click_ready}</li>
            </ul>
          </InstructionsCard>

          {!isValidDistribution() && (
            <ValidationAlert>
              <strong>{dict.invalid_distribution}</strong>
              <br />
              {dict.required_roles}
              <br />
              {dict.current_distribution
                .replace('{0}', String(roleCounts.red || 0))
                .replace('{1}', String(roleCounts.black || 0))
                .replace('{2}', String(roleCounts.don || 0))
                .replace('{3}', String(roleCounts.sheriff || 0))}
            </ValidationAlert>
          )}

          <ActionsFooter>
            <CancelButton onClick={onCancel}>{dict.cancelgame}</CancelButton>
          </ActionsFooter>
        </PhaseMain>
      </PhaseContainer>
    </PhaseBackground>
  );
};

export default RoleAssignment;
